import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { SEO } from '../components/SEO';
import { SlidersHorizontal, X, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';

export const ShopPage = () => {
  const { t, getLocalizedText, categories, API, language } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const currentCategory = searchParams.get('category') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');
  const onSale = searchParams.get('on_sale') === 'true';
  const featured = searchParams.get('featured') === 'true';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (currentCategory) params.append('category', currentCategory);
        if (currentSearch) params.append('search', currentSearch);
        if (onSale) params.append('on_sale', 'true');
        if (featured) params.append('featured', 'true');
        params.append('page', currentPage.toString());
        params.append('limit', '24');

        const res = await fetch(`${API}/products?${params}`);
        const data = await res.json();
        setProducts(data.products || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      } catch (err) {
        console.error('Failed to fetch products', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [API, currentCategory, currentSearch, currentPage, onSale, featured]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const goToPage = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const activeCategory = categories.find(c => c.id === currentCategory);

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <label className="text-sm font-medium mb-2 block">{t('search')}</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={currentSearch}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Categories */}
      <div>
        <label className="text-sm font-medium mb-3 block">{t('categories')}</label>
        <div className="space-y-2">
          <button
            onClick={() => updateFilter('category', '')}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              !currentCategory ? 'bg-primary text-white' : 'hover:bg-gray-100'
            }`}
            data-testid="category-all"
          >
            All Products
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => updateFilter('category', cat.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                currentCategory === cat.id ? 'bg-primary text-white' : 'hover:bg-gray-100'
              }`}
              data-testid={`category-${cat.slug}`}
            >
              {getLocalizedText(cat.name)}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div>
        <label className="text-sm font-medium mb-3 block">{t('filters')}</label>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={onSale}
              onCheckedChange={(checked) => updateFilter('on_sale', checked ? 'true' : '')}
              data-testid="filter-sale"
            />
            <span className="text-sm">{t('onSale')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={featured}
              onCheckedChange={(checked) => updateFilter('featured', checked ? 'true' : '')}
              data-testid="filter-featured"
            />
            <span className="text-sm">{t('featured')}</span>
          </label>
        </div>
      </div>

      {(currentCategory || currentSearch || onSale || featured) && (
        <Button variant="outline" onClick={clearFilters} className="w-full" data-testid="clear-filters">
          <X className="mr-2 h-4 w-4" /> Clear Filters
        </Button>
      )}
    </div>
  );

  // SEO data
  const seoTitle = activeCategory 
    ? getLocalizedText(activeCategory.name)
    : (language === 'ro' ? 'Magazin' : 'Shop');
  const seoDescription = activeCategory?.description 
    ? getLocalizedText(activeCategory.description) 
    : (language === 'ro' 
        ? 'Descoperă colecția noastră de lenjerii de pat premium. Calitate garantată, livrare rapidă.'
        : 'Discover our premium bed linen collection. Guaranteed quality, fast delivery.');

  // Build breadcrumbs for SEO
  const breadcrumbs = [
    { name: t('home'), url: '/' },
    { name: t('shop'), url: '/shop' },
  ];
  if (activeCategory) {
    breadcrumbs.push({ name: getLocalizedText(activeCategory.name) });
  }

  return (
    <Layout>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={activeCategory 
          ? `${getLocalizedText(activeCategory.name)}, lenjerii de pat, cearceafuri, huse pilota`
          : 'lenjerii de pat, cearceafuri, huse pilota, fete perna, magazin online'}
        url={activeCategory ? `/shop?category=${activeCategory.id}` : '/shop'}
        language={language}
        breadcrumbs={breadcrumbs}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8" data-testid="shop-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
          <Link to="/" className="hover:text-primary">{t('home')}</Link>
          <span>/</span>
          <span className="text-foreground">{t('shop')}</span>
          {activeCategory && (
            <>
              <span>/</span>
              <span className="text-foreground">{getLocalizedText(activeCategory.name)}</span>
            </>
          )}
        </nav>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <h2 className="font-heading text-xl font-semibold mb-6">{t('filters')}</h2>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1625]">
                  {activeCategory ? getLocalizedText(activeCategory.name) : t('shop')}
                </h1>
                {activeCategory?.description && getLocalizedText(activeCategory.description) && (
                  <p className="text-muted-foreground mt-2 max-w-2xl">
                    {getLocalizedText(activeCategory.description)}
                  </p>
                )}
                <p className="text-muted-foreground mt-1 text-sm">{total} products</p>
              </div>

              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-btn">
                      <SlidersHorizontal className="h-4 w-4 mr-2" />
                      {t('filters')}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left">
                    <SheetHeader>
                      <SheetTitle>{t('filters')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>

            {/* Active Filters */}
            {(currentCategory || currentSearch || onSale || featured) && (
              <div className="flex flex-wrap gap-2 mb-6">
                {activeCategory && (
                  <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-sm px-3 py-1 rounded-full">
                    {getLocalizedText(activeCategory.name)}
                    <button onClick={() => updateFilter('category', '')}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {currentSearch && (
                  <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-sm px-3 py-1 rounded-full">
                    "{currentSearch}"
                    <button onClick={() => updateFilter('search', '')}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {onSale && (
                  <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-sm px-3 py-1 rounded-full">
                    {t('onSale')}
                    <button onClick={() => updateFilter('on_sale', '')}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square bg-gray-200 rounded-lg mb-3" />
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground mb-4">No products found</p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-12">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {/* First page */}
                {currentPage > 3 && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => goToPage(1)}
                    >
                      1
                    </Button>
                    {currentPage > 4 && <span className="px-2 text-muted-foreground">...</span>}
                  </>
                )}
                
                {/* Pages around current */}
                {[...Array(5)].map((_, i) => {
                  const page = currentPage - 2 + i;
                  if (page < 1 || page > totalPages) return null;
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="icon"
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </Button>
                  );
                })}
                
                {/* Last page */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => goToPage(totalPages)}
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShopPage;
